import React, { useState } from 'react';
import useGameStore from '../store/gameStore';
import { getSecteur } from '../utils/retail';
import { computeMerchFx, secteursReseau, merchSecteur, previsionSaison, MALUS_SECTEUR_NON_ORGANISE } from '../utils/merchandising';
import { exportPlanogrammePdf } from '../utils/exportPlanogrammePdf';
import { LayoutGrid, Route, CalendarClock, AlertTriangle, FileDown, TrendingUp, TrendingDown } from 'lucide-react';
import { PlanogrammeEditor } from '../components/merchandising/PlanogrammeEditor';
import { ParcoursEditor } from '../components/merchandising/ParcoursEditor';
import { toast } from '../components/ui/sonner';

const ONGLETS = [
  { id: 'facing', label: 'Facing & planogramme', icon: LayoutGrid },
  { id: 'parcours', label: 'Parcours client', icon: Route },
];
const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

const couleurScore = (s) => (s == null ? 'text-slate-500' : s >= 75 ? 'text-emerald-400' : s >= 50 ? 'text-amber-400' : 'text-red-400');

export default function Merchandising() {
  const retail = useGameStore(s => s.retail);
  const month = useGameStore(s => s.month) || 1;
  const company = useGameStore(s => s.company);
  const [onglet, setOnglet] = useState('facing');
  const [secteurId, setSecteurId] = useState(null);
  if (!retail) return <div className="text-slate-400 text-sm" data-testid="merch-empty">Aucun magasin actif.</div>;
  const secteurs = secteursReseau(retail);
  const secActif = secteurId && secteurs.includes(secteurId) ? secteurId : retail.secteur;
  const sec = getSecteur(secActif);
  const fx = computeMerchFx(retail, { secteurId: secActif, month });
  const prev = previsionSaison(retail, secActif, month);
  const nonOrganises = secteurs.filter(id => !merchSecteur(retail, id).facing && id !== retail.secteur);
  const nbMagasinsSecteur = (secActif === retail.secteur ? 1 : 0) + (retail.magasins || []).filter(m => (m.secteurId || retail.secteur) === secActif).length;

  const exporter = async () => {
    try {
      await exportPlanogrammePdf({ retail, secteurId: secActif, month, companyName: company?.name });
      toast('Fiche merchandising exportée', { description: `Planogramme et plan du magasin ${sec?.nom} — à afficher en réserve.` });
    } catch (e) {
      toast.error('Export PDF impossible', { description: String(e?.message || e) });
    }
  };

  return (
    <div className="space-y-5" data-testid="merchandising-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="overline">{sec?.nom} · {nbMagasinsSecteur} magasin{nbMagasinsSecteur > 1 ? 's' : ''} du secteur · demande de {MOIS[month - 1]}</p>
          <h1 className="font-display text-2xl md:text-3xl font-black tracking-tight text-foreground mt-0.5 flex items-center gap-2">
            <LayoutGrid className="w-6 h-6 text-accent" /> Merchandising
          </h1>
          <p className="text-[11px] text-slate-500 mt-1 max-w-2xl">
            Deux ateliers interactifs : le <b className="text-slate-300">planogramme</b> décide ce que le client voit en rayon (facings, niveau des yeux),
            le <b className="text-slate-300">parcours client</b> décide ce qu'il traverse (zones chaudes/froides, produit d'appel, caisses).
            La conception d'un secteur s'applique à tous ses magasins ; chaque nouveau secteur ouvert doit être organisé.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-end" data-testid="merch-resume">
          {[
            { label: 'Planogramme', score: fx.facing.score, onglet: 'facing' },
            { label: 'Parcours', score: fx.parcours.score, onglet: 'parcours' },
          ].map(x => (
            <button key={x.label} type="button" onClick={() => setOnglet(x.onglet)} className="rounded border border-ink-700 bg-ink-900/40 px-3 py-1.5 text-left hover:border-accent/60 transition-colors" data-testid={`merch-resume-${x.onglet}`}>
              <p className="text-[9px] uppercase tracking-wider text-slate-500">{x.label}</p>
              <p className={`font-mono text-sm font-bold ${couleurScore(x.score)}`}>{x.score == null ? 'non configuré' : `${x.score}/100`}</p>
            </button>
          ))}
          <button type="button" onClick={exporter} className="rounded border border-accent/60 text-accent px-3 py-2 text-xs font-bold hover:bg-accent/10 flex items-center gap-1.5" data-testid="merch-export-pdf">
            <FileDown className="w-3.5 h-3.5" /> Fiche PDF réserve
          </button>
        </div>
      </div>

      {secteurs.length > 1 && (
        <div className="flex flex-wrap gap-2" data-testid="merch-secteurs">
          {secteurs.map(id => {
            const sc = getSecteur(id);
            const organise = !!merchSecteur(retail, id).facing;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setSecteurId(id)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border flex items-center gap-1.5 transition-colors ${secActif === id ? 'bg-accent text-white border-accent' : 'border-ink-600 text-slate-300 hover:bg-ink-800'}`}
                data-testid={`merch-secteur-${id}`}
              >
                {sc?.emoji} {sc?.nom}
                {!organise && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300">à organiser</span>}
              </button>
            );
          })}
        </div>
      )}

      {nonOrganises.length > 0 && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 flex items-start gap-2" data-testid="merch-alerte-secteur">
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-100">
            {nonOrganises.length === 1 ? 'Un nouveau secteur n\'a pas encore de planogramme' : `${nonOrganises.length} secteurs n'ont pas encore de planogramme`} ({nonOrganises.map(id => getSecteur(id)?.nom || id).join(', ')}) :
            leurs magasins subissent <b>−{Math.round((1 - MALUS_SECTEUR_NON_ORGANISE) * 100)} % de ventes</b> tant que le rayon n'est pas conçu. Sélectionnez le secteur ci-dessus pour l'organiser.
          </p>
        </div>
      )}

      {prev.alerte && (
        <div className="rounded-lg border border-sky-500/40 bg-sky-500/10 p-3 flex items-start gap-2" data-testid="merch-alerte-saison">
          <CalendarClock className="w-4 h-4 text-sky-300 shrink-0 mt-0.5" />
          <div className="text-xs text-sky-100 space-y-1">
            <p className="font-bold">La saison change en {MOIS[prev.suivant - 1]} — anticipez votre planogramme {sec?.nom}.</p>
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              {prev.montent.map(v => <span key={v.famId} className="flex items-center gap-1 text-emerald-300" data-testid={`saison-monte-${v.famId}`}><TrendingUp className="w-3 h-3" /> {v.nom} +{v.pct} %</span>)}
              {prev.descendent.map(v => <span key={v.famId} className="flex items-center gap-1 text-rose-300" data-testid={`saison-descend-${v.famId}`}><TrendingDown className="w-3 h-3" /> {v.nom} {v.pct} %</span>)}
            </div>
            {prev.scoreActuel != null && (
              <p>Score planogramme : <b className={couleurScore(prev.scoreActuel)}>{prev.scoreActuel}/100</b> aujourd'hui → <b className={couleurScore(prev.scorePrevu)} data-testid="saison-score-prevu">{prev.scorePrevu}/100</b> le mois prochain si rien ne change. La suggestion automatique tient compte de la demande du mois en cours.</p>
            )}
          </div>
        </div>
      )}

      <div className="flex gap-1 border-b border-ink-700" data-testid="merch-onglets">
        {ONGLETS.map(o => (
          <button
            key={o.id}
            type="button"
            onClick={() => setOnglet(o.id)}
            className={`px-4 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${onglet === o.id ? 'border-accent text-accent' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
            data-testid={`merch-onglet-${o.id}`}
          >
            <o.icon className="w-3.5 h-3.5" /> {o.label}
          </button>
        ))}
      </div>

      {onglet === 'facing'
        ? <PlanogrammeEditor key={`facing-${secActif}`} secteurId={secActif} />
        : <ParcoursEditor key={`parcours-${secActif}`} secteurId={secActif} />}
    </div>
  );
}
